// DO NOT ADD FEATURE-SPECIFIC LOGIC HERE. See design.md §Extensibility Hooks.

/**
 * `AppProviders` — the single React composition root for the Frontend.
 *
 * ─── Protected extensibility seam (Req 16.7) ────────────────────────────────
 * This module is one of the four protected foundation modules called out in
 * the design (§Extensibility Hooks). Along with `api/client.ts`,
 * `providers/queryClient.ts`, and `router/routes.tsx`, it forms the fixed
 * surface every feature composes against.
 *
 * Feature authors ADD new providers to this file by inserting a new line
 * into the composition between the marked extension slots below. They do
 * NOT replace, refactor, or reorder the existing provider tree, and they
 * do NOT inline route-specific or API-specific logic here.
 *
 * The rules, in one paragraph:
 *   - Every new cross-cutting provider (auth, i18n, dark mode, analytics,
 *     feature flags, offline cache, …) becomes ONE new file under
 *     `src/providers/` and ONE new element in the JSX tree below.
 *   - Route wiring lives in `router/routes.tsx`, not here.
 *   - HTTP/API wiring lives in `api/client.ts`, not here.
 *   - The `queryClient` value is imported from `providers/queryClient.ts`
 *     and passed unchanged to `QueryClientProvider`. Do not construct a
 *     second `QueryClient` in this file.
 *
 * Why the discipline: every feature module in the app relies on this tree
 * looking the same on every render. Reordering `HelmetProvider`,
 * `QueryClientProvider`, or `BrowserRouter` would silently break SEO tag
 * emission, query cache identity, or router context for downstream code.
 *
 * Requirements satisfied:
 *   - Req 16.7: `AppProviders` is the documented composition seam; features
 *     extend the app by adding new providers between the slots below, not
 *     by editing the existing structure.
 *
 * See:
 *   - design.md §Extensibility Hooks (authoritative slot list).
 *   - docs/developer.md (per-slot integration recipes for feature authors).
 */

import type { PropsWithChildren } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

import { HelmetProvider } from './helmetProvider';
import { queryClient } from './queryClient';
import { ThemeProvider } from './ThemeProvider';

/**
 * Composes the fixed provider tree around the app.
 *
 * Consumed once by `src/main.tsx` as `<AppProviders><App /></AppProviders>`.
 * The `children` slot receives the routed application shell.
 */
export function AppProviders({ children }: PropsWithChildren): JSX.Element {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ThemeProvider>
            {children}
          </ThemeProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  );
}
